import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Stack,
  InputGroup,
  InputRightElement,
  useColorModeValue,
} from '@chakra-ui/react';
import { useFormContext, type FieldPathByValue } from 'react-hook-form';
import { FormTextInput } from 'src/components/molecules/formInput/FormTextInput';
import { FormSearchSelect } from 'src/components/molecules/formInput/FormSearchSelect';
import { decodeJwt } from 'jose';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { denormalizeCompanyGroupName } from 'src/contexts/AuthContext/utils';
import { IconButton } from 'src/components/atoms/IconButton';
import { Icon } from 'src/components/atoms/Icon';
import { Country, City } from 'country-state-city';

type AccountDetailFormData = {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;

  // Company Information
  companyName: string;
  companyAddress: string;
  country: {
    value: string;
    label: string;
  };
  city: string;
  customerCategory: string;

  // Product Usage Questions
  yearRoundUsage: string;
  seasonalUsage: string;

  // Project Information
  projectInfo: string;
};

const ADMIN_WATCH_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'password',
  'confirmPassword',
  'companyName',
  'companyAddress',
  'country',
  'city',
  'customerCategory',
  'projectInfo',
] as const;

const USER_WATCH_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'password',
  'confirmPassword',
  'companyName',
] as const;

type AccountDetailStepProps = {
  onValidationChange?: (isValid: boolean) => void;
  setIsJoiningExistingCompany: (isJoiningExistingCompany: boolean) => void;
  isJoiningExistingCompany: boolean;
};

export const AccountDetailStep: React.FC<AccountDetailStepProps> = ({
  onValidationChange,
  setIsJoiningExistingCompany,
  isJoiningExistingCompany,
}) => {
  const [yearRoundUsage, setYearRoundUsage] = useState('yes');
  const [seasonalUsage, setSeasonalUsage] = useState('yes');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const methods = useFormContext<AccountDetailFormData>();
  const { setValue, watch, formState, getValues } = methods;

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get('token');
    if (!token) return;
    try {
      const { email: claimEmail, customerId: claimCustomerId } = decodeJwt(
        token,
      ) as {
        email?: string;
        customerId?: string;
      };

      if (typeof claimEmail === 'string') {
        setValue('email', claimEmail, {
          shouldValidate: true,
          shouldDirty: false,
        });
      }
      // if there is a customerId, we assume the user is trying to join an existing company
      if (claimCustomerId) {
        setIsJoiningExistingCompany(true);
        setValue('companyName', denormalizeCompanyGroupName(claimCustomerId), {
          shouldValidate: true,
          shouldDirty: false,
        });
      }
    } catch {
      // ignore invalid/expired token
    }
  }, [setIsJoiningExistingCompany, setValue]);

  // Watch all required fields
  const watchedFields = !isJoiningExistingCompany
    ? watch(ADMIN_WATCH_FIELDS)
    : watch(USER_WATCH_FIELDS);

  // Check if all required fields are filled
  useEffect(() => {
    const isStringFormValid =
      watchedFields
        .filter((field) => typeof field === 'string')
        .every(
          (field) => field && typeof field === 'string' && field.trim() !== '',
        ) &&
      formState.errors.confirmPassword === undefined &&
      formState.errors.password === undefined;

    const isObjectFormValid = watchedFields
      .filter((field) => field === 'country')
      .every(
        (field) =>
          field && typeof field === 'object' && Object.keys(field).length > 0,
      );

    onValidationChange?.(isStringFormValid && isObjectFormValid);
  }, [
    watchedFields,
    formState.errors.confirmPassword,
    formState.errors.password,
    onValidationChange,
  ]);

  // const bgColor = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  const countryOptions = useMemo(
    () =>
      Country.getAllCountries()
        .map((country) => ({
          value: country.isoCode,
          label: country.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );
  const selectedCountry = watch('country');

  const cityOptions = useMemo(() => {
    if (!selectedCountry?.value) {
      return [];
    }
    return (
      City.getCitiesOfCountry(selectedCountry?.value)
        ?.map((city) => ({
          value: city.name,
          label: city.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [selectedCountry]);

  useEffect(() => {
    const currentCity = getValues('city');

    if (!selectedCountry) {
      if (currentCity) {
        setValue('city', '', { shouldValidate: true, shouldDirty: true });
      }
      return;
    }

    const cityExists = cityOptions.some((city) => city.value === currentCity);
    if (currentCity && !cityExists) {
      setValue('city', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [selectedCountry, cityOptions, getValues, setValue]);

  const handleYearRoundChange = (value: string) => {
    setYearRoundUsage(value);
    setValue('yearRoundUsage', value);
  };

  const handleSeasonalChange = (value: string) => {
    setSeasonalUsage(value);
    setValue('seasonalUsage', value);
  };

  return (
    <Box as="form">
      <VStack spacing={8} align="stretch" mb={{ base: 12, md: 24 }}>
        {/* Plan Selection Section */}
        {/* <Box
            p={6}
            bg={bgColor}
            borderRadius="lg"
            border="1px solid"
            borderColor="transparent"
            cursor="pointer"
            _hover={{ bg: 'gray.100' }}
            transition="all 0.2s">
            <HStack justify="space-between" align="center">
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold" fontSize="lg" color={textColor}>
                  PROFESSIONAL
                </Text>
                <Text color={secondaryTextColor} fontSize="sm">
                  Lorem ipsum dolor sit amet consectetur.
                </Text>
                <HStack align="baseline" spacing={1}>
                  <Text fontWeight="bold" fontSize="xl" color={textColor}>
                    $7.00
                  </Text>
                  <Text fontSize="sm" color={secondaryTextColor}>
                    /Month
                  </Text>
                </HStack>
              </VStack>
              <Icon
                iconName="triangle-down"
                boxSize={5}
                color={secondaryTextColor}
              />
            </HStack>
          </Box> */}

        {/* Personal Information Section */}
        <VStack spacing={6} align="stretch">
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Personal Information
          </Text>

          <HStack spacing={4}>
            <Box flex={1}>
              <FormTextInput<AccountDetailFormData>
                inputPath="firstName"
                inputProps={{ bg: 'surface.hover' }}
                label="First Name *"
                placeholder="Enter First Name"
                required={true}
                helperText=""
              />
            </Box>
            <Box flex={1}>
              <FormTextInput<AccountDetailFormData>
                inputPath="lastName"
                inputProps={{ bg: 'surface.hover' }}
                label="Last Name *"
                placeholder="Enter Last Name"
                required={true}
                helperText=""
              />
            </Box>
          </HStack>

          <FormTextInput<AccountDetailFormData>
            inputPath="email"
            inputProps={{ bg: 'surface.hover' }}
            label="Email *"
            placeholder="Enter Email"
            required={true}
            helperText=""
          />

          <HStack spacing={4} align="flex-start">
            <Box flex={1}>
              <FormControl isInvalid={!!methods.formState.errors.password}>
                <FormLabel
                  color="text.muted"
                  fontWeight="medium"
                  mb={2}
                  fontSize="sm">
                  Password *
                </FormLabel>
                <InputGroup>
                  <Input
                    {...methods.register('password', {
                      required: 'Password is required',
                    })}
                    placeholder="Enter Password"
                    type={showPassword ? 'text' : 'password'}
                    bg="surface.hover"
                    borderColor={
                      methods.formState.errors.password ? 'red' : 'transparent'
                    }
                    borderRadius="md"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{
                      borderColor: methods.formState.errors.password
                        ? 'red'
                        : 'transparent',
                      boxShadow: methods.formState.errors.password
                        ? '0 0 0 1px #CC6478'
                        : '0 0 0 1px var(--chakra-colors-blue-500)',
                    }}
                    _hover={{
                      borderColor: methods.formState.errors.password
                        ? 'red'
                        : 'transparent',
                    }}
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      colorScheme="gray">
                      <Icon
                        iconName={showPassword ? 'view-off' : 'view'}
                        color="gray.500"
                      />
                    </IconButton>
                  </InputRightElement>
                </InputGroup>
                {methods.formState.errors.password && (
                  <FormErrorMessage color="red" fontSize="sm">
                    {methods.formState.errors.password.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            </Box>
            <Box flex={1}>
              <FormControl
                isInvalid={!!methods.formState.errors.confirmPassword}>
                <FormLabel
                  htmlFor="confirmPassword"
                  color="text.muted"
                  fontWeight="medium"
                  mb={2}
                  fontSize="sm">
                  Confirm password *
                </FormLabel>
                <InputGroup>
                  <Input
                    {...methods.register('confirmPassword', {
                      required: 'Confirm password is required',
                      validate: (value) => {
                        const password = methods.watch('password');
                        return value === password || 'Passwords do not match';
                      },
                    })}
                    id="confirmPassword"
                    placeholder="Enter Password Again"
                    type={showConfirmPassword ? 'text' : 'password'}
                    bg="surface.hover"
                    borderColor={
                      methods.formState.errors.confirmPassword
                        ? 'red'
                        : 'transparent'
                    }
                    borderRadius="md"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{
                      borderColor: methods.formState.errors.confirmPassword
                        ? 'red'
                        : 'transparent',
                      boxShadow: methods.formState.errors.confirmPassword
                        ? '0 0 0 1px #CC6478'
                        : '0 0 0 1px var(--chakra-colors-blue-500)',
                    }}
                    _hover={{
                      borderColor: methods.formState.errors.confirmPassword
                        ? 'red'
                        : 'transparent',
                    }}
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label={
                        showConfirmPassword ? 'Hide password' : 'Show password'
                      }
                      h="1.75rem"
                      size="sm"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      variant="ghost"
                      colorScheme="gray">
                      <Icon
                        iconName={showPassword ? 'view-off' : 'view'}
                        color="gray.500"
                      />
                    </IconButton>
                  </InputRightElement>
                </InputGroup>
                {methods.formState.errors.confirmPassword && (
                  <FormErrorMessage color="red" fontSize="sm">
                    {methods.formState.errors.confirmPassword.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            </Box>
          </HStack>
        </VStack>

        {/* Company Information Section */}
        <VStack spacing={6} align="stretch">
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Company Information
          </Text>

          <FormTextInput<AccountDetailFormData>
            inputPath="companyName"
            label="Company Name *"
            placeholder="Enter Company Name"
            required={true}
            inputProps={{
              isDisabled: isJoiningExistingCompany,
              bg: 'surface.hover',
            }}
            helperText=""
          />

          {!isJoiningExistingCompany && (
            <>
              <FormTextInput<AccountDetailFormData>
                inputPath="companyAddress"
                inputProps={{ bg: 'surface.hover' }}
                label="Company Address *"
                placeholder="Enter Company Address"
                required={true}
                helperText=""
              />

              <HStack spacing={4} align="start">
                <Box flex={1}>
                  <FormSearchSelect<AccountDetailFormData, string>
                    inputPath={
                      'country' as FieldPathByValue<
                        AccountDetailFormData,
                        string
                      >
                    }
                    placeholder="Select Country"
                    options={countryOptions}
                    required={true}
                    label="Country *"
                    selectProps={{
                      noOptionsMessage: () => 'No countries found',
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <FormSearchSelect<AccountDetailFormData, string>
                    inputPath="city"
                    placeholder="Select City"
                    options={cityOptions}
                    required={true}
                    label="City *"
                    selectProps={{
                      isDisabled: !selectedCountry?.value,
                      noOptionsMessage: () =>
                        selectedCountry?.value
                          ? 'No cities found'
                          : 'Select a country first',
                    }}
                  />
                </Box>
              </HStack>
              <FormSearchSelect<AccountDetailFormData, string>
                inputPath="customerCategory"
                placeholder="Select Business Category"
                options={[
                  { value: 'fmcg', label: 'FMCG' },
                  { value: 'b2b', label: 'B2B' },
                  { value: 'retail', label: 'Retail' },
                  { value: 'automotive', label: 'Automotive' },
                  { value: 'banking', label: 'Banking' },
                  { value: 'insurance', label: 'Insurance' },
                  { value: 'health', label: 'Health' },
                  { value: 'general', label: 'General' },
                ]}
                required={true}
                label="Business Category *"
                selectProps={{
                  noOptionsMessage: () => 'No categories found',
                }}
              />
            </>
          )}
        </VStack>

        {/* Product Usage Questions Section */}
        {!isJoiningExistingCompany && (
          <>
            <VStack spacing={6} align="stretch">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Product/Service Usage Questions
              </Text>

              <VStack spacing={4} align="start">
                <Text fontWeight="medium" color={secondaryTextColor}>
                  Is your product/service used year-round? *
                </Text>
                <Stack direction="row" spacing={6}>
                  <Checkbox
                    isChecked={yearRoundUsage === 'yes'}
                    onChange={() => handleYearRoundChange('yes')}
                    value="yes"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        Yes
                      </Text>
                    }
                  />
                  <Checkbox
                    isChecked={yearRoundUsage === 'no'}
                    onChange={() => handleYearRoundChange('no')}
                    value="no"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        No
                      </Text>
                    }
                  />
                </Stack>
              </VStack>

              <VStack spacing={4} align="start">
                <Text fontWeight="medium" color={secondaryTextColor}>
                  Is your product/service seasonal or event-driven? *
                </Text>
                <Stack direction="row" spacing={6}>
                  <Checkbox
                    isChecked={seasonalUsage === 'yes'}
                    onChange={() => handleSeasonalChange('yes')}
                    value="yes"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        Yes
                      </Text>
                    }
                  />
                  <Checkbox
                    isChecked={seasonalUsage === 'no'}
                    onChange={() => handleSeasonalChange('no')}
                    value="no"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        No
                      </Text>
                    }
                  />
                  <Checkbox
                    isChecked={seasonalUsage === 'both'}
                    onChange={() => handleSeasonalChange('both')}
                    value="both"
                    label={
                      <Text color={textColor} fontWeight="bold">
                        Both
                      </Text>
                    }
                  />
                </Stack>
              </VStack>
            </VStack>

            {/* Project Information Section */}
            <VStack spacing={6} align="stretch">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Project Information
              </Text>

              <FormControl>
                <FormLabel
                  color="text.muted"
                  fontWeight="medium"
                  mb={2}
                  fontSize="sm">
                  Information about your project *
                </FormLabel>
                <Textarea
                  placeholder="Enter information about your project"
                  size="md"
                  rows={4}
                  resize="vertical"
                  bg="surface.hover"
                  borderColor="transparent"
                  borderRadius="md"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                  }}
                  _hover={{
                    borderColor: 'gray.300',
                  }}
                  {...methods.register('projectInfo', { required: 'Required' })}
                />
              </FormControl>
            </VStack>
          </>
        )}
      </VStack>
    </Box>
  );
};
